import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Infinity as InfinityIcon, Smartphone, FileSignature, HandHeart, CheckCircle2, Globe2 } from "lucide-react";
import { ASSETS } from "@/data/assets";
import { useLiveStats } from "@/hooks/useLiveStats";
import { loadIdentity } from "@/lib/identity";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Returning-signee detection — pulls /api/me/journey when localStorage has
// an identity so the Hero primary CTA can switch from "Sign the Declaration"
// to "Welcome back, [Name] — Add a Conscious Act". DB-driven, never assumed.
function ReturningSigneeCTAs({ stats }) {
  const [identity, setIdentity] = useState(loadIdentity);
  const [journey, setJourney] = useState(null);

  useEffect(() => {
    const onChange = () => setIdentity(loadIdentity());
    window.addEventListener("docm:identity-changed", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("docm:identity-changed", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  useEffect(() => {
    if (!identity?.email) { setJourney(null); return; }
    let cancelled = false;
    axios.get(`${API}/me/journey`, { params: { email: identity.email } })
      .then((r) => { if (!cancelled) setJourney(r.data || null); })
      .catch(() => { if (!cancelled) setJourney(null); });
    return () => { cancelled = true; };
  }, [identity?.email]);

  const hasSigned = !!(journey && journey.signatures > 0);
  const firstName = (identity?.name || "").split(" ")[0] || "";

  const scrollToSign = (e) => {
    e.preventDefault();
    const el = document.getElementById("sign");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (hasSigned) {
    return (
      <div className="mt-6" data-testid="hero-returning-signee-block">
        {/* High-contrast returning-signer banner — filled gold pill so the
            message reads clearly against the dark hero video (previous
            moss-tinted version failed AA contrast per the bug report). */}
        <div
          data-testid="hero-returning-banner"
          className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-docm-gold text-docm-midnight text-[12px] uppercase tracking-[0.16em] font-semibold shadow-[0_10px_25px_-12px_rgba(201,169,97,0.6)]"
        >
          <CheckCircle2 size={13} />
          <span>{firstName ? `Welcome back, ${firstName}` : "Welcome back"} · You signed the Declaration</span>
        </div>
        <div className="mt-4 flex flex-wrap gap-3 md:gap-4">
          <Link
            to="/random-acts?path=individual#rac-form"
            data-testid="hero-primary-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-docm-gold text-docm-midnight font-sans font-semibold px-7 py-3.5 text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:bg-docm-goldSoft group shadow-[0_18px_45px_-15px_rgba(201,169,97,0.6)]"
          >
            <HandHeart size={16} />
            Perform a Random Act of Consciousness
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/random-acts#wall"
            data-testid="hero-secondary-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-transparent border border-docm-gold/50 text-docm-gold font-sans font-medium px-7 py-3.5 text-sm tracking-wide transition-all duration-300 hover:border-docm-gold hover:bg-docm-gold/10"
          >
            See the Consciousness Wall
          </Link>
        </div>
        <SignatureCountStrip stats={stats} />
      </div>
    );
  }

  // First-time visitor — primary CTA leads to Random Acts (the main action),
  // secondary leads to Sign the Declaration (the philosophical foundation).
  return (
    <>
      <div className="mt-7 flex flex-wrap gap-3 md:gap-4">
        <Link
          to="/random-acts?path=individual#rac-form"
          data-testid="hero-primary-cta"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-docm-gold text-docm-midnight font-sans font-semibold px-7 py-3.5 text-sm tracking-wide transition-all duration-300 hover:-translate-y-0.5 hover:bg-docm-goldSoft group shadow-[0_18px_45px_-15px_rgba(201,169,97,0.6)]"
        >
          <HandHeart size={16} />
          Perform a Random Act
          <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
        <a
          href="#sign"
          onClick={scrollToSign}
          data-testid="hero-secondary-cta"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-transparent border border-docm-gold/50 text-docm-gold font-sans font-medium px-7 py-3.5 text-sm tracking-wide transition-all duration-300 hover:border-docm-gold hover:bg-docm-gold/10"
        >
          <FileSignature size={15} />
          Sign the Declaration
        </a>
      </div>
      <SignatureCountStrip stats={stats} />
    </>
  );
}

// Compact live-count strip — Tiger 2026-07-08: show only the country
// count ("Signatures across N countries so far"), NOT the raw signature
// number. Renders only when countries > 0.
function SignatureCountStrip({ stats }) {
  const countries = stats?.total_countries || 0;
  if (countries === 0) return null;
  return (
    <div
      data-testid="hero-signature-count"
      className="mt-5 inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-[13.5px] text-docm-cream/85"
    >
      <Globe2 size={13} className="text-docm-gold" />
      <span>
        Signatures across{" "}
        <strong data-testid="hero-signature-count-countries" className="text-docm-cream font-semibold tabular-nums">
          {countries}
        </strong>{" "}
        {countries === 1 ? "country" : "countries"} so far
      </span>
    </div>
  );
}

export default function Hero() {
  const stats = useLiveStats();
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[86vh] flex items-center pt-20 md:pt-24 pb-14 md:pb-20 overflow-hidden"
    >
      {/* Background video */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="w-full h-full object-cover md:scale-[1.22] md:translate-x-[9%]"
          data-testid="hero-video"
        >
          <source src={ASSETS.heroVideo} type="video/mp4" />
        </video>
        {/* Cinematic gradient over video — balanced so the underlying
            visuals breathe through without becoming a competing image. */}
        <div className="absolute inset-0 bg-gradient-to-br from-docm-midnight/65 via-docm-midnight/40 to-docm-ink/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-docm-midnight/75 via-transparent to-docm-midnight/20" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 w-full">
        {/* FULL-VIEWPORT-CENTERED movement badge — lifted OUT of the content
            column so it sits dead-centre above the hero, regardless of the
            text alignment of the headline. */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="flex justify-center mb-5 md:mb-7"
        >
          <div
            data-testid="hero-movement-badge"
            className="inline-flex items-center gap-3 px-6 md:px-7 py-2.5 md:py-3 rounded-full bg-docm-cream/10 border border-docm-gold/45 backdrop-blur-sm"
          >
            <InfinityIcon size={17} className="text-docm-gold" />
            <span className="text-[14px] md:text-[15px] uppercase tracking-[0.24em] text-docm-gold font-semibold">
              Declaration of Consciousness Movement
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <h1 className="h-display text-[38px] sm:text-5xl lg:text-[64px] xl:text-[72px] text-docm-cream leading-[1.04] tracking-tight">
            Declare{" "}
            <span className="font-display italic text-docm-goldSoft">Consciousness.</span>
            <br />
            Do one act of{" "}
            <span className="font-display italic text-docm-goldSoft">goodness.</span>
            <br />
            Uplift{" "}
            <span className="font-display italic text-docm-gold">humanity.</span>
          </h1>

          <p className="mt-5 max-w-2xl text-base md:text-[17px] text-docm-cream/85 leading-relaxed font-sans">
            A global movement that turns awareness into action. Sign the Declaration,
            perform one Random Act of Consciousness, and be part of{" "}
            <span className="text-docm-gold font-semibold">11·11 — the world&rsquo;s Consciousness Day</span>.
          </p>

          {/* Primary + secondary CTAs — primary opens the choose-path gate
              at /random-acts; secondary scrolls to the Declaration signing form. */}
          <ReturningSigneeCTAs stats={stats} />

          <Link
            to="/conscious-super-app"
            data-testid="hero-app-pill"
            className="mt-5 inline-flex items-center gap-2 text-docm-cream/75 hover:text-docm-gold text-[13px] font-medium transition-colors group"
          >
            <span className="inline-flex w-7 h-7 rounded-full bg-docm-cream/10 group-hover:bg-docm-gold/15 items-center justify-center transition-colors">
              <Smartphone size={13} />
            </span>
            <span>Or get the Conscious Super App</span>
            <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>

      {/* Anthem ribbon at bottom */}
      <div className="absolute bottom-0 left-0 right-0 border-t border-docm-gold/20 bg-docm-midnight/60 backdrop-blur-sm overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee py-3.5 font-serif-h text-base md:text-lg text-docm-gold/80 italic">
          {Array.from({ length: 2 }).map((_, blk) => (
            <div key={blk} className="flex shrink-0">
              {[
                "One Humanity.",
                "One Consciousness.",
                "One Shared Future.",
                "Random Acts of Consciousness.",
              ].map((t) => (
                <span key={t + blk} className="px-8 flex items-center gap-8">
                  {t}
                  <span className="text-docm-gold">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
